import { useEffect, useState, Suspense, useMemo, useCallback, ReactElement } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { Collection } from '../../lib/api'
import { getFeaturedCollections, getCdnUrl } from '../../lib/api'
import Breadcrumb from '../../components/Breadcrumb'
import Carousel3D from './Carousel3D'
import type { CarouselItem } from './Carousel3D'
import './FeaturedCarousel.css'

/**
 * Featured Collections 3D Carousel Container
 *
 * Fetches top collections from the API and displays them in an interactive 3D carousel.
 * Handles loading, error, and empty states with appropriate UI feedback.
 *
 * @returns {React.ReactElement}
 */
const FeaturedCarouselContainer = (): ReactElement => {
  const navigate = useNavigate()
  const [collections, setCollections] = useState<Collection[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFeaturedCollections = async () => {
      try {
        setLoading(true)
        const data = await getFeaturedCollections(12)
        setCollections(data)
        setError(null)
      } catch (err) {
        console.error('Failed to fetch featured collections:', err)
        setError(err instanceof Error ? err.message : 'Failed to load collections')
      } finally {
        setLoading(false)
      }
    }

    fetchFeaturedCollections()
  }, [])

  /**
   * Handle collection card click - navigate to collection page
   * @param {CarouselItem} item - The carousel item data
   * @param {number} index - Item index
   */
  const handleCollectionClick = useCallback((item: CarouselItem, index: number): void => {
    const collection = item.collection
    if (collection.contractAddress) {
      navigate({
        to: '/collection/$slug',
        params: { slug: collection.contractAddress },
      })
    } else if (collection.id) {
      navigate({
        to: '/collection/$slug',
        params: { slug: collection.id },
      })
    }
  }, [navigate])

  /**
   * Get image URL for collection banner/logo
   * @param {Collection} collection
   * @returns {string}
   */
  const getCollectionImage = (collection: Collection): string => {
    const imagePath =
      collection.banner ||
      collection.logo ||
      collection.imageUrl ||
      collection.image ||
      collection.coverImage

    if (imagePath) {
      if (imagePath.startsWith('http')) {
        return imagePath
      }
      const path = imagePath.startsWith('/') ? imagePath : `/${imagePath}`
      return getCdnUrl(path)
    }
    return ''
  }

  /**
   * Format collection stats for display
   * @param {Collection} collection
   * @returns {{ items: string, popularity: string }}
   */
  const formatCollectionStats = (collection: Collection): { items: string; popularity: string } => {
    const totalSupply = collection.totalSupply || 0
    const popScore = collection.popScore || 0

    return {
      items: totalSupply.toLocaleString(),
      popularity: Math.round(popScore).toLocaleString(),
    }
  }

  const carouselItems = useMemo<CarouselItem[]>(() => {
    return collections.map((collection, index) => {
      const stats = formatCollectionStats(collection)
      return {
        id: collection.id || collection.contractAddress || index,
        image: getCollectionImage(collection),
        title: collection.name || `Collection ${index + 1}`,
        symbol: collection.symbol || '',
        stats: stats,
        collection: collection,
      }
    })
  }, [collections])

  if (loading) {
    return (
      <>
        <Breadcrumb text1='Featured' text2='Featured Collections' />
        <main className="main-content mt-1 border-radius-lg">
          <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading featured collections...</p>
            </div>
          </div>
        </main>
      </>
    )
  }

  if (error) {
    return (
      <>
        <Breadcrumb text1='Featured' text2='Featured Collections' />
        <main className="main-content mt-1 border-radius-lg">
          <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <p className="text-danger">Error loading collections: {error}</p>
              <button className="btn btn-primary mt-3" onClick={() => window.location.reload()}>
                Retry
              </button>
            </div>
          </div>
        </main>
      </>
    )
  }

  if (collections.length === 0) {
    return (
      <>
        <Breadcrumb text1='Featured' text2='Featured Collections' />
        <main className="main-content mt-1 border-radius-lg">
          <div className="container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <p className="text-muted">No featured collections available at the moment.</p>
            </div>
          </div>
        </main>
      </>
    )
  }

  return (
    <>
      <Breadcrumb text1='Featured' text2='Featured Collections' />
      <main className="main-content mt-1 border-radius-lg featured-page">
        
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div id="carousel-container">
                <Suspense fallback={<div className="loading-3d">Loading 3D Carousel...</div>}>
                  <Carousel3D
                    items={carouselItems}
                    onItemClick={handleCollectionClick}
                    visibleItems={5}
                    rotationSpeed={0.5}
                  />
                </Suspense>
              </div>

              <div id="collection-info" className="text-center mt-4">
                <p className="text-muted">
                  Click on a collection to view details
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}

export default FeaturedCarouselContainer
